// @owners { team: patients-team }
import React, { useCallback, useContext } from 'react';
import { ActionSheetContext } from '../../../../../design-system/src';
import { CartUpdatedActionSheet } from '../../../../../features';
import { useRemoveFromCart } from '../../../../../features/src/cart/hooks/useRemoveFromCart';
import { enqueueRemoveUnschedulablePrescriptionsWork } from '~shared/features/cart/hooks/removeUnschedulablePrescriptionsChain';
import { usePrescriptionsInCart } from '~shared/features/cart/hooks/usePrescriptionsInCart';
import { getDefaultAddress } from '~shared/features/checkout/selectors/getDefaultAddress';
import { getItemsInOrder } from '~shared/features/checkout/selectors/getItems';
import { getOrderAddressId } from '~shared/features/checkout/selectors/getOrder';
import { getIsScheduleableByPrescriptionID } from '~shared/features/prescriptions/selectors/getScheduleableForPrescriptionID';
import { getIsEditingExistingOrder } from '~shared/features/ui/selectors/getCart';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useSelectorShared, useStore } from '~shared/store';
import { type ReduxStateShared } from '~shared/types';

export { resetRemoveUnschedulablePrescriptionsChainForTests } from '~shared/features/cart/hooks/removeUnschedulablePrescriptionsChain';

/**
 * Unschedulable removals are serialized in {@link enqueueRemoveUnschedulablePrescriptionsWork} (e.g. route transition
 * remounts when the dashboard wraps `<Outlet />` in `TransitionGroup` + `CSSTransition`). Each job re-reads Redux so a
 * follow-up job skips lines already removed from checkout.
 */
export const useRemoveUnschedulablePrescriptionsFromCart = () => {
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const { trackEvent } = useAnalytics();
  const { handleRemoveFromCart } = useRemoveFromCart();
  const { prescriptions } = usePrescriptionsInCart();
  const store = useStore();
  const isEditingOrder = useSelectorShared(getIsEditingExistingOrder);

  const removeUnschedulablePrescriptions = useCallback(() => {
    // if we are editing an order, we are in post checkout and should not remove unschedulable meds
    if (isEditingOrder) return;

    // `getCartDetails` appends one entry per cart line; duplicate lines for the same prescription produce
    // duplicate `prescriptions` entries and would otherwise call DELETE /v1/cart/cart_items/:id twice for the same item.
    const prescriptionsById = new Map(prescriptions.map((p) => [p.id, p]));
    const uniquePrescriptions = [...prescriptionsById.values()];

    enqueueRemoveUnschedulablePrescriptionsWork(async () => {
      let removedItems = false;

      for (const prescription of uniquePrescriptions) {
        const state = store.getState() as ReduxStateShared;

        if (getIsEditingExistingOrder(state)) {
          return;
        }

        const nextAvailableDateAddressID = getOrderAddressId(state) || getDefaultAddress(state)?.id;
        const isScheduleableByPrescriptionID = getIsScheduleableByPrescriptionID(state, {
          nextAvailableDateAddressID,
        });

        const stillInCheckoutOrder = getItemsInOrder(state).some((item) => item.prescription.id === prescription.id);
        if (!stillInCheckoutOrder || isScheduleableByPrescriptionID[prescription.id]) {
          continue;
        }

        removedItems = true;
        trackEvent({
          event: EVENTS.CART__UNSCHEDULABLE_PRESCRIPTION_REMOVED,
          params: {},
          additionalFields: { prescriptionId: prescription.id },
        });

        await handleRemoveFromCart({
          item: {
            resource_type: 'Prescription',
            resource_id: prescription.id,
            prescription,
          },
        });
      }

      if (removedItems) {
        setActiveActionSheet(<CartUpdatedActionSheet />);
      }
    });
  }, [isEditingOrder, prescriptions, trackEvent, handleRemoveFromCart, setActiveActionSheet, store]);

  return { removeUnschedulablePrescriptions };
};
